import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// Components
import {
  OverlayPanel,
  OverlayPanelHeader,
  Panel,
  Text,
  ColoredCountUp,
  Pill,
  If,
} from '../../../../components'

// Hooks
import { useListingDisplay } from '../../../../hooks/listing'
import { useBuyerSettings } from '../../../../hooks/buyerSettings'

// Helpers
import { formatAbbrCurrency } from '../../../../helpers'

// Styles
import styles from './ComparedToStaying.module.scss'

const formattedCostOrSavings = cost => {
  // Negative cost == positive savings
  return {
    color: cost < 0 ? 'green' : 'red',
    symbol: cost < 0 ? '+' : '-',
    copy: cost < 0 ? 'gain' : 'lose',
  }
}

const ComparedToStaying = ({ handleBack }) => {
  const { yearsInHome, totalBuyCost, totalCurrentCost } = useListingDisplay()
  const { currentAddress: currentHomeAddress } = useBuyerSettings()

  const [activeSubNav, setActiveSubNav] = useState('thisHome')

  const formattedBuyCost = formattedCostOrSavings(totalBuyCost)
  const formattedCurrentCost = formattedCostOrSavings(totalCurrentCost)

  return (
    <OverlayPanel>
      <OverlayPanelHeader onBack={handleBack}>
        <Text element='span' color='white' weight='semibold' size='l'>
          Financial Forecast
        </Text>
      </OverlayPanelHeader>

      <div className={styles.subnav}>
        <Pill
          onClick={() => setActiveSubNav('thisHome')}
          className={classnames(
            styles.pill,
            activeSubNav === 'thisHome' && styles.active
          )}
        >
          This home
        </Pill>
        <Pill
          onClick={() => setActiveSubNav('currentHome')}
          className={classnames(
            styles.pill,
            activeSubNav === 'currentHome' && styles.active
          )}
        >
          {currentHomeAddress}
        </Pill>
      </div>

      <If condition={activeSubNav === 'thisHome'}>
        <Panel className={styles.panel}>
          <Text color='eggShell' size='m' upperspaced>
            {`You might ${formattedBuyCost.copy} around`}
          </Text>
          <Text color='eggShell' size='xxl'>
            <Text color={formattedBuyCost.color} element='span'>
              {formattedBuyCost.symbol}
            </Text>
            <ColoredCountUp
              weight='light'
              value={totalBuyCost * -1}
              formatter={formatAbbrCurrency}
            />
          </Text>
          <Text color='white'>
            if you sold{' '}
            <Text weight='bold' element='span' color='white'>
              {currentHomeAddress}
            </Text>{' '}
            today, then bought this home and sold it after{' '}
            <Text weight='bold' element='span' color='white'>
              {` ${yearsInHome} years`}
            </Text>
          </Text>
        </Panel>
      </If>

      <If condition={activeSubNav === 'currentHome'}>
        <Panel className={styles.panel}>
          <Text color='eggShell' size='m' upperspaced>
            {`You might ${formattedCurrentCost.copy} around`}
          </Text>
          <Text color='eggShell' size='xxl'>
            <Text color={formattedCurrentCost.color} element='span'>
              {formattedCurrentCost.symbol}
            </Text>
            <ColoredCountUp
              weight='light'
              value={totalCurrentCost * -1}
              formatter={formatAbbrCurrency}
            />
          </Text>
          <Text color='white'>
            if you stayed and sold{' '}
            <Text weight='bold' element='span' color='white'>
              {currentHomeAddress}
            </Text>{' '}
            after{' '}
            <Text weight='bold' element='span' color='white'>
              {` ${yearsInHome} ${yearsInHome === 1 ? 'year' : 'years'}`}
            </Text>
          </Text>
        </Panel>
      </If>
    </OverlayPanel>
  )
}

ComparedToStaying.propTypes = {
  handleBack: PropTypes.func,
}

export default ComparedToStaying
